// REACT IMPORTS
import React, { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { isPasswordValid } from "utils/HelperFunctions";
import { useAppDispatch } from "store/hooks";
import {
  passwordReset,
  updateTokenExpiredStatus,
  updateTokenStatus,
} from "store/slices/authSlice";

// ICONS IMPORTS
import { CircleFillTickIcon } from "icons/success/CircleFillTickIcon";
import { CloseRingIcon } from "icons/subscription/CloseRingIcon";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { onSetIsLogin } from "store/slices/commonSlice";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- LOGIN VIA CRED FORM ------------------------------------- */

const ResetPasswordForm = () => {
  const tid = useTid();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { clusterId: cluster_id } = useParams();
  const initialVal = {
    password: "",
    r_password: "",
  };
  const [isLoading, setLoading] = useState<boolean>(false);
  const [values, setValues] = useState<any>(initialVal);

  const onSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      let payload = {
        ...values,
        accounts_id: state?.accounts_id?.accounts_id,
        otpToken: state?.otpToken,
      };
      delete payload["r_password"];
      let res: any = await dispatch(passwordReset(payload)).unwrap();
      if (res && res?.body?.success) {
        localStorage.setItem(TOKEN_STORAGE_KEY, res?.body?.token);
        localStorage.setItem(TOKEN_STORAGE_TIME, `${new Date().getTime()}`);
        dispatch(updateTokenStatus(false));
        dispatch(updateTokenExpiredStatus(false));
        dispatch(onSetIsLogin(true));
        navigate(`/${cluster_id}/dashboard`);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.log("reset pass err: -->", error?.message);
    }
  };

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });

  const handleChange = (e: any) => {
    const password = e.target.value;
    setValues({ ...values, password });
    const requirements = isPasswordValid(password);
    setPasswordRequirements(requirements);
  };

  const checkIsDisabled = () => {
    let val = Object.values(values).every((value) => value === "");
    let isRequirementsEmpty = Object.values(passwordRequirements).every(
      (value) => value === true
    );
    let checkpass = values?.r_password !== values?.password;
    return val || !isRequirementsEmpty || checkpass;
  };

  return (
    <div
      className="animate__animated animate__fadeIn"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[2%] leading-normal text-txtPrimary">
        Forgot Password
      </div>
      <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph text-sm lg:text-base leading-normal font-normal">
        Please ensure your password meets the below criteria
      </p>
      <form onSubmit={onSubmit} data-testid={tid("reset-password-form")}>
        <div className="max-w-[520px] flex flex-col gap-10 lg:gap-[60px]">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col lg:gap-3 gap-5">
              <InputField
                type="password"
                name="password"
                label="New password"
                data-testid={tid("new-password-input")}
                aria-label="New password"
                placeholder="Enter new password"
                value={values?.password}
                onChange={(e: any) => handleChange(e)}
                _className="!font-medium"
              />
              <div
                className="lg:p-4 py-4 px-3 bg-bgThird rounded-[7px] select-none"
                data-testid={tid("password-requirements")}
                aria-label="Password requirements"
              >
                <div>Password Requirements</div>
                <div className="pt-3">
                  <div className="flex items-center gap-2">
                    {passwordRequirements?.length ? (
                      <CircleFillTickIcon aria-hidden="true" />
                    ) : (
                      <CloseRingIcon aria-hidden="true" />
                    )}
                    <div className="py-1 text-txtSecondary text-sm lg:text-base">
                      Minimum 8 characters.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {passwordRequirements?.uppercase ? (
                      <CircleFillTickIcon aria-hidden="true" />
                    ) : (
                      <CloseRingIcon aria-hidden="true" />
                    )}
                    <div className="py-1 text-txtSecondary text-sm lg:text-base">
                      At least one uppercase letter (A-Z).
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {passwordRequirements?.lowercase ? (
                      <CircleFillTickIcon aria-hidden="true" />
                    ) : (
                      <CloseRingIcon aria-hidden="true" />
                    )}
                    <div className="py-1 text-txtSecondary text-sm lg:text-base">
                      At least one lowercase letter (a-z).
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {passwordRequirements?.digit ? (
                      <CircleFillTickIcon aria-hidden="true" />
                    ) : (
                      <CloseRingIcon aria-hidden="true" />
                    )}
                    <div className="py-1 text-txtSecondary text-sm lg:text-base">
                      At least one numerical digit (0-9).
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {passwordRequirements?.specialChar ? (
                      <CircleFillTickIcon aria-hidden="true" />
                    ) : (
                      <CloseRingIcon aria-hidden="true" />
                    )}
                    <div className="py-1 text-txtSecondary text-sm lg:text-base">
                      At least one special character (e.g., !, @, #, $, etc.)
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <InputField
                type="password"
                name="r_password"
                label="re-enter New password"
                data-testid={tid("confirm-password-input")}
                aria-label="Confirm new password"
                placeholder="Re-enter new password"
                value={values?.r_password}
                onChange={(e: any) =>
                  setValues({ ...values, r_password: e.target.value })
                }
                _className="!font-medium"
              />
              {values?.r_password &&
                values?.password !== values?.r_password && (
                  <div
                    className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                    role="alert"
                    data-testid={tid("password-mismatch-alert")}
                  >
                    <InfoRedCircleIcon aria-hidden="true" />
                    <div className="lg:text-base text-sm text-red leading-[20.83px]">
                      Passwords do not match.
                    </div>
                  </div>
                )}
            </div>
          </div>
          <Button
            isLoading={isLoading}
            className="w-full"
            text="Change Password"
            type="submit"
            data-testid={tid("change-password-button")}
            aria-label="Change password"
            isDisabled={checkIsDisabled()}
            variant="variable"
          />
        </div>
        <div className="py-5 lg:text-base text-sm text-blue hover:!text-btnPrimaryHover leading-normal font-medium flex justify-center">
          <div
            className="cursor-pointer"
            onClick={() => navigate(-1)}
            onKeyDown={activateOnKey(() => navigate(-1))}
            role="button"
            tabIndex={0}
            data-testid={tid("go-back-button")}
            aria-label="Go back"
          >
            Go back
          </div>
        </div>
        <div className="w-full h-[1px] bg-strokePrimary" />
      </form>
      <div className="max-w-[520px] flex flex-col gap-4 items-center pt-4">
        <div className="flex gap-[6px] items-center justify-start w-full">
          <p className="lg:text-base text-sm text-txtParagraph leading-normal">
            Remember your password?
          </p>
          <Link
            to={`/${cluster_id}/login`}
            className="cursor-pointer lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium leading-normal"
            data-testid={tid("login-link")}
            aria-label="Log in instead"
          >
            Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
